/* ============================================================================
   LIVE STORE — one cached fetch of every live dataset the QA backend exposes,
   shared by all live screens (Overview, Circles, Live QA…).
   ----------------------------------------------------------------------------
   The backend has NO admin/global read surface — every read is scoped to the
   authenticated user (their own data) or public. So the live datasets here are:
     - me / app_meta            (profile + global counters)
     - my_meditations_v2        (the user's own meditations, upcoming + past)
     - meditations_v2(public)   (the GLOBAL public meditation feed = live circles)
     - circles(only_own:false)  (the user's own + member-of flat circle groups)
     - my_purchases             (the user's purchases / contributions)
   Anything not derivable from these (cross-user analytics, occupancy capacity,
   income per circle, org rollups) has no backend source and is flagged DEMO in
   the UI. See documentation/plan/07 §11.
   ============================================================================ */
import React from 'react';
import { gqlQuery } from '../auth/api';

const QUERY = `
  query ConsoleLive {
    me {
      id username full_name email
      total_time_meditated premium_level following_count followers_count
    }
    app_meta { count_users_online count_meditations }
    myUpcoming: my_meditations_v2(page: 1, page_size: 50) { total_count items { ...Med } }
    myPast: my_meditations_v2(page: 1, page_size: 50, show_past: true) { total_count items { ...Med } }
    publicMeds: meditations_v2(page: 1, page_size: 50, only_public: true) { total_count items { ...Med } }
    myCircles: circles(only_own: false) { id name count_members user { id username } }
    my_purchases(page: 1, page_size: 50) {
      total_count
      items {
        purchase_id purchase_type product_id status
        amount_minor currency_code created_at
        meditation_id meditation_title host_display_name contribution_item_label
      }
    }
  }
  fragment Med on Meditation {
    id title start_time duration is_private central_object repeat_interval
    count_participants count_live_participants count_reminded_users
    user { id username full_name } circle { id name count_members }
  }
`;

export interface LiveMed {
  id: string;
  title?: string | null;
  start_time: string;
  duration: number;
  is_private: boolean;
  central_object: string;
  repeat_interval?: string | null;
  count_participants: number;
  count_live_participants: number;
  count_reminded_users: number;
  user?: { id: string; username: string; full_name?: string | null } | null;
  circle?: { id: string; name: string; count_members: number } | null;
}
export interface LivePurchase {
  purchase_id: string;
  purchase_type: string;
  product_id: string;
  status: string;
  amount_minor?: number | null;
  currency_code?: string | null;
  created_at: string;
  meditation_id?: string | null;
  meditation_title?: string | null;
  host_display_name?: string | null;
  contribution_item_label?: string | null;
}
interface RawLive {
  me: {
    id: string;
    username: string;
    full_name?: string | null;
    email?: string | null;
    total_time_meditated: number;
    premium_level?: number | null;
    following_count: number;
    followers_count: number;
  } | null;
  app_meta: { count_users_online: number; count_meditations: number };
  myUpcoming: { total_count: number; items: LiveMed[] };
  myPast: { total_count: number; items: LiveMed[] };
  publicMeds: { total_count: number; items: LiveMed[] };
  myCircles: Array<{ id: string; name: string; count_members: number; user?: { id: string; username: string } | null }> | null;
  my_purchases: { total_count: number; items: LivePurchase[] };
}

/** Unified "circle" view model — a recurring/public meditation OR a flat group. */
export interface LiveCircle {
  id: string;
  name: string;
  host: string;
  schedule: string;
  members: number;
  attended: number | null; // live participants (meditations only)
  invited: number | null;
  isPrivate: boolean;
  source: 'meditation' | 'group';
  startTime?: string;
}

export interface LiveBundle {
  me: RawLive['me'];
  appMeta: { online: number; meditations: number };
  circles: LiveCircle[];
  publicMedsTotal: number;
  myUpcoming: LiveMed[];
  myPast: LiveMed[];
  myMedsTotal: number;
  purchases: LivePurchase[];
  purchasesTotal: number;
  raw: RawLive;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtSchedule(startTime?: string | null, repeat?: string | null): string {
  if (!startTime) return '—';
  const d = new Date(startTime);
  if (Number.isNaN(d.getTime())) return String(startTime);
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return repeat ? `${base} · ${repeat}` : base;
}

function medToCircle(m: LiveMed): LiveCircle {
  return {
    id: m.id,
    name: m.title || '(untitled)',
    host: m.user?.username || m.user?.full_name || '—',
    schedule: fmtSchedule(m.start_time, m.repeat_interval),
    members: m.count_participants,
    attended: m.count_live_participants,
    invited: m.count_participants,
    isPrivate: m.is_private,
    source: 'meditation',
    startTime: m.start_time,
  };
}

function mapBundle(raw: RawLive): LiveBundle {
  const groups: LiveCircle[] = (raw.myCircles || []).map((c) => ({
    id: c.id,
    name: c.name,
    host: c.user?.username || 'you',
    schedule: '—',
    members: c.count_members,
    attended: null,
    invited: null,
    isPrivate: false,
    source: 'group',
  }));
  const meds = (raw.publicMeds?.items || []).map(medToCircle);
  const seen = new Set<string>();
  const circles = [...meds, ...groups].filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
  return {
    me: raw.me,
    appMeta: { online: raw.app_meta?.count_users_online ?? 0, meditations: raw.app_meta?.count_meditations ?? 0 },
    circles,
    publicMedsTotal: raw.publicMeds?.total_count ?? meds.length,
    myUpcoming: raw.myUpcoming?.items || [],
    myPast: raw.myPast?.items || [],
    myMedsTotal: (raw.myUpcoming?.total_count ?? 0) + (raw.myPast?.total_count ?? 0),
    purchases: raw.my_purchases?.items || [],
    purchasesTotal: raw.my_purchases?.total_count ?? 0,
    raw,
  };
}

let cache: Promise<LiveBundle> | null = null;

/** Load (and cache) the live bundle. `force` re-fetches. Shared across screens. */
export function loadLive(force = false): Promise<LiveBundle> {
  if (force) cache = null;
  if (!cache) cache = gqlQuery<RawLive>(QUERY).then(mapBundle);
  return cache;
}

export interface LiveState {
  loading: boolean;
  error: string | null;
  data: LiveBundle | null;
  reload: () => void;
}

/** Hook: returns the shared live bundle, with loading/error and a reload(). */
export function useLive(): LiveState {
  const [state, setState] = React.useState<{ loading: boolean; error: string | null; data: LiveBundle | null }>({
    loading: true,
    error: null,
    data: null,
  });
  const run = React.useCallback((force?: boolean) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    loadLive(force)
      .then((d) => setState({ loading: false, error: null, data: d }))
      .catch((e: unknown) => setState({ loading: false, error: e instanceof Error ? e.message : String(e), data: null }));
  }, []);
  React.useEffect(() => {
    run();
  }, [run]);
  return { ...state, reload: () => run(true) };
}
